import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import {
  SpreadsheetData,
  SpreadsheetEditor,
} from "@via/components/spreadsheet-editor/spreadsheet-editor.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";
import { type Options } from "react-select";
import { DataSourceProvider, GeoItem } from "common";
import { FieldFormData } from "@via/pages/data/premium-dataset/utils/helper.ts";
import {
  fetchFileCollectionFields,
  useCreatePremiumDatasetFile,
  useGetFileCollectionId,
  useGetProjectDatasetFields,
  usePremiumDatasetFileList,
} from "@via/pages/data/premium-dataset/premium-dataset.api.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner.tsx";
import AdditionalFieldsModal from "@via/pages/data/premium-dataset/components/additional-fields-modal.tsx";
import ManualAddFieldsModal from "@via/pages/data/premium-dataset/components/manual-add-fields-modal.tsx";
import { useFileOrganizer } from "@via/hooks/useFileOrganizer.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import {
  convertEmptyStringsToNull,
  filterColumnsByCollectionType,
  formatSpreadsheetData,
  mapBackupFormDataToFileObject,
  spreadsheetToObjects,
} from "@via/pages/data/file-organizer/helpers.ts";
import {
  buildPremiumFilePayload,
  type FileFieldNameLabel,
} from "@via/pages/data/file-organizer/helpers.ts";
import { ROUTES } from "@via/utils/constants.tsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SampleTrackerViewType } from "@via/pages/sample-trackers/sample-trackers-filter";
import { classNames } from "@via/utils/helpers.tsx";

// Import custom hooks and utilities
import { useVmetaProjectManagement } from "./hooks/useVmetaProjectManagement.ts";
import { useFieldManagement } from "./hooks/useFieldManagement.ts";
import { validateBeforeSave } from "./utils/validation.ts";
import FileOrganizerBackupForm, {
  IFileOrganizerBackupForm,
} from "@via/pages/data/file-organizer/file-organizer-backup-form.tsx";
import { formatSharedTrackerOptionLabel } from "@via/pages/data/utils/shared-tracker-option-label";

interface PremiumSampleOrganizerProps {
  collectionType: string;
  provider: DataSourceProvider;
  selectedGeoList?: GeoItem[];
  credentialId?: number | null;
  serviceTokenId?: number;
  dataSourceName?: string;
}

const PremiumSampleOrganizer: React.FC<PremiumSampleOrganizerProps> = ({
  collectionType,
  provider,
  selectedGeoList,
  credentialId,
  serviceTokenId,
  dataSourceName,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setNotification } = useNotifications();
  const { selectedSamples, triggerFullReset } = useFileOrganizer();
  const { selectedCollectionId: contextCollectionId } = useGlobal();

  // Read datasetId from URL params, fallback to context
  const selectedCollectionId =
    searchParams.get("datasetId") || contextCollectionId;

  const [selectedTrackerViewType, setSelectedTrackerViewType] =
    useState<SampleTrackerViewType>(SampleTrackerViewType.My);

  const { data: datasetFilesData } = usePremiumDatasetFileList(
    selectedCollectionId as string,
    0,
    1
  );

  const initialProjectId = useMemo(
    () => datasetFilesData?.data?.[0]?.canvasId as string | undefined,
    [datasetFilesData?.data]
  );

  // Use custom hook for vmeta project management
  const {
    vmetaProjectOptions,
    selectedVmetaProject,
    setSelectedVmetaProject,
    isLoading: isVmetaProjectsLoading,
    setSearch,
    fetchMoreOptions,
    createProject,
    resolvedInitialTrackerViewType,
  } = useVmetaProjectManagement({
    userId: user?.vmeta?.userId,
    viewType: selectedTrackerViewType,
    initialProjectId,
    resolveInitialProjectOwnership: true,
  });

  useEffect(() => {
    if (!resolvedInitialTrackerViewType) return;
    setSelectedTrackerViewType(resolvedInitialTrackerViewType);
  }, [resolvedInitialTrackerViewType]);

  // Local state for spreadsheet and columns
  const [columnLabels, setColumnLabels] = useState<string[]>([]);
  const [spreadsheetData, setSpreadsheetData] = useState<SpreadsheetData>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [collectionId, setCollectionId] = useState("");
  const [showAdditionalFieldsModal, setShowAdditionalFieldsModal] =
    useState(false);
  const [showManualAddFieldsModal, setShowManualAddFieldsModal] =
    useState(false);
  const [additionalColumns, setAdditionalColumns] = useState<string[]>([]);
  const [backupFormData, setBackupFormData] = useState<
    IFileOrganizerBackupForm | undefined
  >(undefined);
  // Name and label of every file field of the selected canvas. Undefined until loaded, and left
  // undefined if loading fails: the source is then not sent, and the save still works.
  const [fileFieldNames, setFileFieldNames] = useState<
    FileFieldNameLabel[] | undefined
  >(undefined);

  // Memoize the callback to prevent infinite loops
  const handleBackupFormChange = useCallback(
    (formData: IFileOrganizerBackupForm) => {
      setBackupFormData(formData);
    },
    []
  );

  // Get project dataset fields
  const { data: vmetaDatasetFields, mutateAsync: getProjectDatasetFields } =
    useGetProjectDatasetFields();

  // Use custom hook for field management
  const { createFields: createFieldsWithRefresh } = useFieldManagement({
    selectedVmetaProject,
    collectionId,
  });

  // API hooks
  const {
    mutateAsync: createPremiumDatasetFile,
    isPending: isCreatePremiumDatasetFileLoading,
  } = useCreatePremiumDatasetFile();
  const { mutateAsync: getFileCollectionId } = useGetFileCollectionId();

  // Memoize filtered column labels
  const filteredColumnLabels = useMemo(
    () =>
      filterColumnsByCollectionType(
        columnLabels,
        collectionType,
        provider,
        fileFieldNames
      ),
    [columnLabels, collectionType, provider, fileFieldNames]
  );

  // Transform GeoItem data to match expected format (srrId -> GEO ID, name -> name)
  const transformedGeoData = useMemo(() => {
    if (!selectedGeoList || selectedGeoList.length === 0) {
      return [];
    }
    return selectedGeoList.map(geoItem => ({
      "GEO ID": geoItem.srrId,
      name: geoItem.name,
    }));
  }, [selectedGeoList]);

  // Determine which data source to use
  const dataSource =
    selectedGeoList !== undefined ? transformedGeoData : selectedSamples;

  // Update spreadsheet data when samples or columns change
  useEffect(() => {
    if (dataSource && dataSource.length > 0) {
      setSpreadsheetData(
        formatSpreadsheetData(filteredColumnLabels, dataSource)
      );
    } else if (filteredColumnLabels.length > 0) {
      // Create one empty row if no rows exist
      const emptyRow = filteredColumnLabels.map(() => ({ value: "" }));
      setSpreadsheetData([emptyRow]);
    }
  }, [dataSource, filteredColumnLabels]);

  // Load fields when project is selected
  useEffect(() => {
    if (selectedVmetaProject?.value) {
      getProjectDatasetFields({
        canvasId: selectedVmetaProject.value as string,
        collectionName: "file",
      });
    }
  }, [selectedVmetaProject, getProjectDatasetFields]);

  useEffect(() => {
    const canvasId = selectedVmetaProject?.value as string | undefined;
    setFileFieldNames(undefined);
    if (!canvasId) return;
    let cancelled = false;
    fetchFileCollectionFields(canvasId)
      .then(fields => {
        if (!cancelled) setFileFieldNames(fields);
      })
      .catch(() => {
        // Unknown field list: no source keys are sent and the save still works.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedVmetaProject]);

  // Update column labels when fields are loaded or refreshed
  // The spreadsheet will be created/updated by the useEffect that watches dataSource and filteredColumnLabels
  useEffect(() => {
    if (vmetaDatasetFields?.data) {
      const fields = vmetaDatasetFields.data[0] || [];
      setColumnLabels(fields);
      // Spreadsheet will be automatically recreated by the useEffect at line 123-133
      // when filteredColumnLabels updates (which depends on columnLabels)
    }
  }, [vmetaDatasetFields]);

  // Handle project change
  const handleVmetaProjectChange = useCallback(
    (selected: SelectOption | Options<SelectOption>) => {
      // Handle both single select and multi-select cases
      const selectedOption = Array.isArray(selected)
        ? selected[0] || null
        : (selected as SelectOption | null);
      setSelectedVmetaProject(selectedOption);
      setErrors([]);
      // Only reset sample list if not using selectedGeoList
      // triggerFullReset will reset both global state and trigger list components to reset their local state
      if (!selectedGeoList) {
        triggerFullReset();
      }
    },
    [setSelectedVmetaProject, triggerFullReset, selectedGeoList]
  );

  // Handle file save with improved error handling
  const handleConfirm = useCallback(async () => {
    let files: Record<string, unknown>[] =
      spreadsheetToObjects(filteredColumnLabels, spreadsheetData) || [];

    // For GEO provider, map collectionType to "File Layout" field
    if (
      provider === DataSourceProvider.GEO &&
      selectedGeoList &&
      selectedGeoList.length > 0
    ) {
      // Create a map of GEO ID (srrId) to collectionType
      const geoIdToCollectionType = new Map<string, string>();
      selectedGeoList.forEach(geoItem => {
        geoIdToCollectionType.set(geoItem.srrId, geoItem.collectionType);
      });

      // Add "File Layout" field to each file based on its GEO ID
      files = files.map(file => {
        const geoId = file["GEO ID"] as string;
        if (geoId && geoIdToCollectionType.has(geoId)) {
          return {
            ...file,
            "File Layout": geoIdToCollectionType.get(geoId),
          };
        }
        return file;
      });
    }

    // Validate before proceeding
    const validation = validateBeforeSave(
      selectedVmetaProject,
      selectedCollectionId,
      spreadsheetData,
      filteredColumnLabels
    );

    if (!validation.isValid) {
      setErrors(validation.errors);
      setNotification({
        id: "validation-error",
        message: "Validation failed",
        details: validation.errors.join(", "),
        variant: NOTIFICATION.ERROR,
      });
      return;
    }

    setErrors([]);

    try {
      // Convert empty strings to null before sending to backend
      const filesWithNulls = files.map(file => convertEmptyStringsToNull(file));

      // Map backup form data to file object format
      const archiveFields = mapBackupFormDataToFileObject(backupFormData);

      // Add archive directory fields to each file object
      const filesWithArchiveAndProviderFields = filesWithNulls.map(file =>
        buildPremiumFilePayload({
          file,
          archiveFields,
          provider,
          credentialId,
          serviceTokenId,
          dataSourceName,
          fileFields: fileFieldNames,
        })
      );

      const results = await Promise.allSettled(
        filesWithArchiveAndProviderFields.map((file, index) =>
          createPremiumDatasetFile({
            id: selectedCollectionId!,
            canvasId: selectedVmetaProject!.value as string,
            file,
          }).catch(err => {
            return Promise.reject({ error: err, index });
          })
        )
      );

      // Check for failures
      const failures = results
        .map((result, index) =>
          result.status === "rejected" ? { index, error: result.reason } : null
        )
        .filter((f): f is { index: number; error: unknown } => f !== null);

      if (failures.length > 0) {
        const errorMessages = failures.map(f => {
          // Extract the actual error from the wrapped error object
          const actualError =
            f.error && typeof f.error === "object" && "error" in f.error
              ? (f.error as { error: unknown }).error
              : f.error;

          // Parse axios error response structure
          if (
            actualError &&
            typeof actualError === "object" &&
            "response" in actualError
          ) {
            const axiosError = actualError as {
              response?: {
                data?: {
                  message?: string;
                  errors?: Array<{ field?: string; message?: string }>;
                };
              };
            };

            // Check for errors array in response
            if (
              axiosError.response?.data?.errors &&
              Array.isArray(axiosError.response.data.errors) &&
              axiosError.response.data.errors.length > 0
            ) {
              const errorDetails = axiosError.response.data.errors
                .map(
                  err =>
                    `${err.field ?? "unknown field"}: ${err.message ?? "unknown error"}`
                )
                .join("; ");
              return `File ${f.index + 1}: ${errorDetails}`;
            }

            // Fall back to message if available
            if (axiosError.response?.data?.message) {
              return `File ${f.index + 1}: ${axiosError.response.data.message}`;
            }
          }

          // Fall back to error message if it's an Error object
          if (actualError instanceof Error) {
            return `File ${f.index + 1}: ${actualError.message}`;
          }

          // Last resort: check if there's a message property
          if (
            actualError &&
            typeof actualError === "object" &&
            "message" in actualError
          ) {
            return `File ${f.index + 1}: ${String(actualError.message)}`;
          }

          return `File ${f.index + 1}: Unknown error`;
        });
        setErrors(errorMessages);
        setNotification({
          id: "partial-save-failure",
          message: `Failed to save ${failures.length} of ${files.length} files`,
          details: errorMessages.join(", "),
          variant: NOTIFICATION.ERROR,
        });
        return;
      }

      // Success
      setNotification({
        id: "save-success",
        message: `Successfully saved ${files.length} file(s)`,
        variant: NOTIFICATION.SUCCESS,
      });

      navigate(`${ROUTES.PREMIUM_COLLECTIONS}/${selectedCollectionId}`);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unable to process files";

      setNotification({
        id: "error-transforming-data",
        message: "Unable to process files",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
    }
  }, [
    filteredColumnLabels,
    spreadsheetData,
    selectedVmetaProject,
    selectedCollectionId,
    createPremiumDatasetFile,
    navigate,
    setNotification,
    provider,
    selectedGeoList,
    backupFormData,
    credentialId,
    serviceTokenId,
    dataSourceName,
    fileFieldNames,
  ]);

  // Handle creating additional fields
  const handleCreateFields = useCallback(
    async (
      fieldsToCreate: FieldFormData[],
      _fieldDataMap: Record<string, FieldFormData>
    ) => {
      try {
        await createFieldsWithRefresh(fieldsToCreate);
        setShowAdditionalFieldsModal(false);
        setAdditionalColumns([]);
        // Clear spreadsheet data - it will be recreated with new columns via useEffect
        setSpreadsheetData([]);
        // Reset selected samples if not using selectedGeoList
        if (!selectedGeoList) {
          triggerFullReset();
        }
        // Manually refetch fields to ensure columnLabels updates
        if (selectedVmetaProject?.value) {
          const updatedFields = await getProjectDatasetFields({
            canvasId: selectedVmetaProject.value as string,
            collectionName: "file",
          });
          if (updatedFields?.data) {
            const fields = updatedFields.data[0] || [];
            setColumnLabels(fields);
          }
        }
      } catch (error) {
        // Error is already handled in the hook
        console.error("Failed to create additional fields:", error);
      }
    },
    [
      createFieldsWithRefresh,
      selectedGeoList,
      triggerFullReset,
      selectedVmetaProject,
      getProjectDatasetFields,
    ]
  );

  const handleSkipFieldCreation = useCallback(() => {
    setShowAdditionalFieldsModal(false);
    setAdditionalColumns([]);
  }, []);

  // Handle manual field addition
  const handleManualAddFields = useCallback(async () => {
    try {
      if (!selectedVmetaProject?.value) {
        setNotification({
          id: "no-project-selected",
          message: "Please select a metadata tracker first",
          variant: NOTIFICATION.ERROR,
        });
        return;
      }

      const id = await getFileCollectionId(
        selectedVmetaProject.value as string
      );
      setCollectionId(id);
      setShowManualAddFieldsModal(true);
    } catch (error) {
      console.error("Failed to get collection ID:", error);
      setNotification({
        id: "collection-id-error",
        message: "Failed to Get Collection Information",
        details:
          "Could not retrieve collection information for field creation.",
        variant: NOTIFICATION.ERROR,
      });
    }
  }, [selectedVmetaProject, getFileCollectionId, setNotification]);

  const handleManualFieldsCreated = useCallback(
    async (fieldsToCreate: FieldFormData[]) => {
      try {
        await createFieldsWithRefresh(fieldsToCreate);
        setShowManualAddFieldsModal(false);
        // Clear spreadsheet data - it will be recreated with new columns via useEffect
        setSpreadsheetData([]);
        // Reset selected samples if not using selectedGeoList
        if (!selectedGeoList) {
          triggerFullReset();
        }
        // Manually refetch fields to ensure columnLabels updates
        if (selectedVmetaProject?.value) {
          const updatedFields = await getProjectDatasetFields({
            canvasId: selectedVmetaProject.value as string,
            collectionName: "file",
          });
          if (updatedFields?.data) {
            const fields = updatedFields.data[0] || [];
            setColumnLabels(fields);
          }
        }
      } catch (error) {
        // Error is already handled in the hook
        console.error("Failed to create manual fields:", error);
      }
    },
    [
      createFieldsWithRefresh,
      selectedGeoList,
      triggerFullReset,
      selectedVmetaProject,
      getProjectDatasetFields,
    ]
  );

  return (
    <div>
      <div className="flex-2 min-w-80 my-4">
        <label
          className="mb-2 block text-sm font-medium leading-6 text-gray-900"
          htmlFor="vmeta-project-select">
          Select Sample Tracker
        </label>
        <div className="mb-1">
          <span className="isolate inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() =>
                setSelectedTrackerViewType(SampleTrackerViewType.My)
              }
              className={classNames(
                "relative inline-flex h-9 items-center justify-center px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 whitespace-nowrap rounded-l-md min-w-[110px]",
                selectedTrackerViewType === SampleTrackerViewType.My
                  ? "bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md"
                  : "bg-white"
              )}>
              My Trackers
            </button>
            <button
              type="button"
              onClick={() =>
                setSelectedTrackerViewType(SampleTrackerViewType.Shared)
              }
              className={classNames(
                "relative inline-flex h-9 items-center justify-center px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 whitespace-nowrap -ml-px min-w-[150px] rounded-r-md",
                selectedTrackerViewType === SampleTrackerViewType.Shared
                  ? "bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md"
                  : "bg-white"
              )}>
              Shared With Me
            </button>
          </span>
        </div>
        <div className="flex items-baseline gap-2">
          <div className="flex-1">
            <SelectComponent
              name="vmetaProjectId"
              options={vmetaProjectOptions}
              value={selectedVmetaProject}
              onChange={handleVmetaProjectChange}
              formatOptionLabel={(option, context) =>
                formatSharedTrackerOptionLabel(
                  option,
                  context,
                  selectedTrackerViewType
                )
              }
              isLoading={isVmetaProjectsLoading}
              isCreatable={selectedTrackerViewType === SampleTrackerViewType.My}
              showCreateHint={
                selectedTrackerViewType === SampleTrackerViewType.My
              }
              onCreateOption={
                selectedTrackerViewType === SampleTrackerViewType.My
                  ? createProject
                  : undefined
              }
              placeholder={
                selectedTrackerViewType === SampleTrackerViewType.My
                  ? "Choose sample tracker or type to create"
                  : "Choose a sample tracker shared with you"
              }
              enableClear={false}
              onFetchMoreOptions={fetchMoreOptions}
              onInputChange={setSearch}
              aria-label="Select metadata tracker"
            />
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              disabled={!selectedVmetaProject}
              onClick={handleManualAddFields}
              className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Add new fields to metadata tracker">
              Add Fields
            </button>
          </div>
        </div>
      </div>
      {errors.length > 0 && (
        <div
          className="my-4 max-h-[250px] overflow-auto"
          role="alert"
          aria-live="polite">
          <MessageBanner messages={errors} type={BannerType.Error} />
        </div>
      )}
      <div className="mb-5">
        <SpreadsheetEditor
          columnLabels={filteredColumnLabels}
          data={spreadsheetData}
          isLoading={isCreatePremiumDatasetFileLoading}
          disableContextMenu
          onChange={newData => setSpreadsheetData(newData)}
        />
      </div>

      <FileOrganizerBackupForm onFormChange={handleBackupFormChange} />

      <div className="flex justify-end mt-4">
        <button
          id="create-button"
          type="button"
          onClick={e => {
            e.preventDefault();
            handleConfirm();
          }}
          disabled={isCreatePremiumDatasetFileLoading || !selectedVmetaProject}
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed">
          {isCreatePremiumDatasetFileLoading ? "Saving..." : "Save Files"}
        </button>
      </div>
      {showAdditionalFieldsModal && (
        <AdditionalFieldsModal
          isOpen={showAdditionalFieldsModal}
          onClose={() => setShowAdditionalFieldsModal(false)}
          additionalColumns={additionalColumns}
          onFieldsCreated={handleCreateFields}
          onSkip={handleSkipFieldCreation}
        />
      )}

      {showManualAddFieldsModal && (
        <ManualAddFieldsModal
          isOpen={showManualAddFieldsModal}
          onClose={() => setShowManualAddFieldsModal(false)}
          onFieldsCreated={handleManualFieldsCreated}
        />
      )}
    </div>
  );
};

export default PremiumSampleOrganizer;
